import React from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { ChevronRight, Users, ArrowRight } from 'lucide-react'
import type { Service } from '../../types'

interface ServiceCardProps {
  service: Service
  onServiceClick: (service: Service) => void
  showChildren?: boolean
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service, onServiceClick, showChildren = true }) => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'

  // Use localized data from API if available, fallback to manual selection
  const title = service.localizedName || (isRTL ? service.title_ar : service.title_en)
  const description = service.localizedDescription || (isRTL ? service.description_ar : service.description_en)
  const tags = service.tags || []

  // Extract first word and rest of title
  const titleParts = title.split(' ')
  const firstWord = titleParts[0] || ''
  const restOfTitle = titleParts.slice(1).join(' ')

  const hasChildren = service.has_children && showChildren
  const childrenCount = service.children_count || 0

  return (
    <Card 
      className="h-full flex flex-col transition-all duration-300 hover:shadow-lg hover:scale-105 cursor-pointer bg-gradient-to-br from-broxel-light to-green-50 hover:to-blue-50 rounded-xl border border-green-100" 
      onClick={() => onServiceClick(service)}
    >
      {service.image_url && (
        <div className="relative overflow-hidden rounded-t-lg">
          <img
            src={service.image_url}
            alt={title}
            className="w-full h-56 object-contain transition-transform duration-300 hover:scale-110"
          />
          {/* <div className="absolute top-4 right-4">
            <Badge variant="secondary" className="bg-white/90 text-foreground">
              {service.category}
            </Badge>
          </div> */}
          {hasChildren && (
            <div className="absolute top-4 left-4">
              <Badge className="bg-primary/90 text-white flex items-center gap-1">
                <Users className="h-3 w-3" />
                {childrenCount} services
              </Badge>
            </div>
          )}
        </div>
      )}
      
      <CardHeader className="flex-shrink-0">
        <div className="flex items-start justify-between">
          <CardTitle className="text-xl font-bold text-foreground flex-1">
            <div className="inline-flex items-center gap-1" dir="ltr">
              <img src="/images/branding/b-logo.webp" alt="B" className="h-5 w-5 inline-block" />
              <span>{firstWord}</span>
            </div>
            {restOfTitle && <span className="ml-1">{restOfTitle}</span>}
          </CardTitle>
          {hasChildren && (
            <ChevronRight className="h-5 w-5 text-muted-foreground ml-2 flex-shrink-0" />
          )}
        </div>
        <CardDescription className="text-muted-foreground line-clamp-3">
          {description}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-grow">
        {/* Display tags */}
        {tags && tags.length > 0 && (
          <div className="mb-4">
            <h4 className="font-semibold text-sm text-foreground mb-3">
              {t('services.capabilities')}:
            </h4>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="text-xs bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                >
                  {isRTL ? tag.tag_ar : tag.tag_en}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="flex-shrink-0 pt-4">
        {hasChildren ? (
          <Button className="w-full" onClick={() => onServiceClick(service)}>
            <span>Explore {childrenCount} Services</span>
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button className="w-full" onClick={() => onServiceClick(service)}>
            {t('services.viewDetails')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </CardFooter>
    </Card>
  )
}

export default ServiceCard
